import { useCallback, useEffect, useState } from 'react';
import { Timestamp } from '@bufbuild/protobuf/wkt';
import { convertTimestampToDate } from '../utils/dateUtils';
import { useAuth } from '../AuthContext';
import { useClient } from '../connect_api';
import { UserInfo, UserService } from '../api/api_pb';
import { useNavigate } from 'react-router-dom';
import { Menu, LogOut, Building2, ChevronDown, Key } from 'lucide-react';
import OrgSwitcher from './OrgSwitcher';

interface HeaderProps {
    onMenuToggle: () => void;
}

const createTimestamp = (date: Date): Timestamp => {
    return {
        $typeName: 'google.protobuf.Timestamp',
        seconds: BigInt(Math.floor(date.getTime() / 1000)),
        nanos: date.getMilliseconds() * 1000000,
    } as Timestamp;
};

const defaultUserInfo: UserInfo = {
    $typeName: 'lease.merchant.v1.UserInfo',
    uuid: '',
    name: 'xxx',
    email: 'xxx@gmail.com',
    status: 0,
};

const Header: React.FC<HeaderProps> = ({ onMenuToggle }) => {
    const { token, logout } = useAuth();
    const [currentDate, setCurrentDate] = useState('');
    const [showLogout, setShowLogout] = useState(false);
    const [userInfo, setUserInfo] = useState<UserInfo>(defaultUserInfo);
    const userClient = useClient(UserService);
    const navigate = useNavigate();

    const fetchUserInfo = useCallback(async (): Promise<void> => {
        if (token) {
            try {
                const response = await userClient.getMyInfo({ });
                setUserInfo(response.userInfo || defaultUserInfo);
            } catch (error) {
                console.error('Failed to fetch user info:', error);
                setUserInfo(defaultUserInfo);
            }
        } else {
            setUserInfo(defaultUserInfo);
        }
    }, [token, userClient]);

    useEffect(() => {
        const now = new Date();
        const timestamp = createTimestamp(now);
        setCurrentDate(convertTimestampToDate(timestamp));
        fetchUserInfo();
    }, [fetchUserInfo]);

    const handleLogout = () => {
        logout();
    };

    if (!token) {
        return null; // Don't show header when not logged in
    }

    return (
        <header className="bg-white shadow-md fixed top-0 left-0 right-0 z-50 h-16">
            <div className="flex justify-between items-center h-full px-4 lg:px-6">
                {/* Left section */}
                <div className="flex items-center gap-4">
                    <button 
                        onClick={onMenuToggle} 
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors lg:hidden"
                        aria-label="Toggle menu"
                    >
                        <Menu className="w-6 h-6 text-gray-700" />
                    </button>
                    <div className="hidden lg:block">
                        <h1 className="text-xl font-semibold text-gray-800">Lease Admin Portal</h1>
                        <p className="text-xs text-gray-500">{currentDate}</p>
                    </div>
                </div>

                {/* Center section - Org switcher */}
                <OrgSwitcher />

                {/* Right section - User menu */}
                <div className="relative">
                    <button 
                        onClick={() => setShowLogout(!showLogout)} 
                        className="flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors group"
                    >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                            {userInfo.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="text-left hidden lg:block">
                            <p className="text-sm font-medium text-gray-800">{userInfo.name}</p>
                            <p className="text-xs text-gray-500">{userInfo.email}</p>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showLogout ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {showLogout && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                            <button
                                onClick={() => {
                                    setShowLogout(false);
                                    navigate('/edit-organization-info');
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                            >
                                <Building2 className="w-4 h-4" />
                                Organization
                            </button>

                            <button
                                onClick={() => {
                                    setShowLogout(false);
                                    navigate('/change-password');
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                            >
                                <Key className="w-4 h-4" />
                                Change Password
                            </button>

                            <div className="border-t border-gray-200 my-1"></div>

                            <button
                                onClick={handleLogout}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                            >
                                <LogOut className="w-4 h-4" />
                                Logout
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;